import React from 'react';
import { Wallet, TrendingUp, TrendingDown, Lock, DollarSign, Info, Layers, Activity, Package } from 'lucide-react';

interface WalletBreakdownCardProps {
  totalBalance: number;
  usedMargin: number;
  futuresUsedMargin: number;
  propUsedMargin: number;
  futuresOrdersReserved: number;
  propOrdersReserved: number;
  unrealizedPnl: number;
  availableBalance: number;
  robotAllocatedBalance: number;
  stakedAmount: number;
  loading?: boolean;
  showDetails?: boolean;
}

const WalletBreakdownCard: React.FC<WalletBreakdownCardProps> = ({
  totalBalance,
  usedMargin,
  futuresUsedMargin,
  propUsedMargin,
  futuresOrdersReserved,
  propOrdersReserved,
  unrealizedPnl,
  availableBalance,
  robotAllocatedBalance,
  stakedAmount,
  loading = false,
  showDetails = true
}) => {
  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || !isFinite(amount)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="rounded-2xl p-6 border shadow-2xl h-full" style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(0, 0, 0, 0.1)'
      }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{
            background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
          }}>
            <Wallet size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>Wallet Breakdown</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 rounded w-3/4" style={{ background: 'rgba(0, 0, 0, 0.1)' }}></div>
          <div className="h-4 rounded w-1/2" style={{ background: 'rgba(0, 0, 0, 0.1)' }}></div>
          <div className="h-4 rounded w-2/3" style={{ background: 'rgba(0, 0, 0, 0.1)' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-6 border shadow-2xl h-full flex flex-col" style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid rgba(0, 0, 0, 0.1)'
    }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{
          background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
        }}>
          <Wallet size={20} className="text-white" />
        </div>
        <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>Wallet Breakdown</h3>
      </div>

      <div className="space-y-4 flex-1">
        {/* Total Balance */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign size={16} style={{ color: '#3b82f6' }} />
            <span style={{ color: '#475569' }}>Total Balance</span>
          </div>
          <span className="font-bold text-lg" style={{ color: '#1e293b' }}>{formatCurrency(totalBalance)}</span>
        </div>

        {showDetails && (
          <>
            {/* Used Margin with breakdown */}
            {usedMargin > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Lock size={16} style={{ color: '#f97316' }} />
                    <span style={{ color: '#475569' }}>Used Margin</span>
                  </div>
                  <span className="font-medium" style={{ color: '#f97316' }}>{formatCurrency(usedMargin)}</span>
                </div>

                {/* Margin breakdown */}
                <div className="ml-6 space-y-1">
                  {futuresUsedMargin > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Activity size={12} style={{ color: '#fb923c' }} />
                        <span style={{ color: '#64748b' }}>Futures Positions</span>
                      </div>
                      <span style={{ color: '#fb923c' }}>{formatCurrency(futuresUsedMargin)}</span>
                    </div>
                  )}
                  {propUsedMargin > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Activity size={12} style={{ color: '#fb923c' }} />
                        <span style={{ color: '#64748b' }}>Prop Positions</span>
                      </div>
                      <span style={{ color: '#fb923c' }}>{formatCurrency(propUsedMargin)}</span>
                    </div>
                  )}
                  {futuresOrdersReserved > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Package size={12} style={{ color: '#fb923c' }} />
                        <span style={{ color: '#64748b' }}>Futures Orders</span>
                      </div>
                      <span style={{ color: '#fb923c' }}>{formatCurrency(futuresOrdersReserved)}</span>
                    </div>
                  )}
                  {propOrdersReserved > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Package size={12} style={{ color: '#fb923c' }} />
                        <span style={{ color: '#64748b' }}>Prop Orders</span>
                      </div>
                      <span style={{ color: '#fb923c' }}>{formatCurrency(propOrdersReserved)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Unrealized PnL */}
            {unrealizedPnl !== 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {unrealizedPnl >= 0 ? (
                    <TrendingUp size={16} style={{ color: '#22c55e' }} />
                  ) : (
                    <TrendingDown size={16} style={{ color: '#ef4444' }} />
                  )}
                  <span style={{ color: '#475569' }}>Unrealized PnL</span>
                </div>
                <span className="font-medium" style={{ color: unrealizedPnl >= 0 ? '#22c55e' : '#ef4444' }}>
                  {unrealizedPnl >= 0 ? '+' : ''}{formatCurrency(unrealizedPnl)}
                </span>
              </div>
            )}

            {/* Robot Allocated Balance */}
            {robotAllocatedBalance > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Lock size={16} style={{ color: '#a855f7' }} />
                  <span style={{ color: '#475569' }}>Reserved (Robot)</span>
                </div>
                <span className="font-medium" style={{ color: '#a855f7' }}>{formatCurrency(robotAllocatedBalance)}</span>
              </div>
            )}

            {/* Staked Amount */}
            {stakedAmount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Layers size={16} style={{ color: '#6366f1' }} />
                  <span style={{ color: '#475569' }}>Staked Assets</span>
                </div>
                <span className="font-medium" style={{ color: '#6366f1' }}>{formatCurrency(stakedAmount)}</span>
              </div>
            )}

            {/* Divider */}
            <div className="border-t my-4" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}></div>
          </>
        )}

        {/* Available Balance */}
        <div className="flex justify-between items-center rounded-xl p-4 border" style={{
          background: 'rgba(34, 197, 94, 0.05)',
          border: '1px solid rgba(34, 197, 94, 0.2)'
        }}>
          <div className="flex items-center gap-2">
            <Wallet size={16} style={{ color: '#22c55e' }} />
            <span className="font-semibold" style={{ color: '#1e293b' }}>Available Balance</span>
          </div>
          <span className="font-bold text-xl" style={{ color: '#22c55e' }}>{formatCurrency(availableBalance)}</span>
        </div>

        {/* Info note */}
        <div className="rounded-xl p-3 flex items-start gap-2" style={{
          background: 'rgba(59, 130, 246, 0.05)',
          border: '1px solid rgba(59, 130, 246, 0.2)'
        }}>
          <Info size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
          <div className="text-xs" style={{ color: '#3b82f6' }}>
            <p className="font-medium mb-1">Available Balance</p>
            <p>This is the amount you can withdraw, stake, or use for trading. Used margin, reserved funds (robot), and staked assets are deducted from your total balance.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletBreakdownCard;